import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  GitMerge,
  AlertTriangle,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  User,
  Building,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  IndianRupee,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FieldResolver } from './FieldResolver';
import { DuplicateSet, MergeConfig } from '@/types/duplicates';
import { Lead } from '@/types/lead';
import { cn } from '@/lib/utils';

interface MergePanelProps {
  set: DuplicateSet;
  onMerge: (config: MergeConfig) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Fields that can be resolved
const MERGEABLE_FIELDS = [
  { key: 'name', label: 'Lead Name', icon: User },
  { key: 'company', label: 'Company', icon: Building },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'phoneNumber', label: 'Phone Number', icon: Phone },
  { key: 'location', label: 'Location', icon: MapPin },
  { key: 'industry', label: 'Industry', icon: Briefcase },
  { key: 'companySize', label: 'Company Size', icon: Building },
  { key: 'contactPerson', label: 'Contact Person', icon: User },
  { key: 'linkedinUrl', label: 'LinkedIn URL', icon: User },
  { key: 'dealValue', label: 'Deal Value', icon: IndianRupee },
  { key: 'expectedClose', label: 'Expected Close', icon: Calendar },
  { key: 'priority', label: 'Priority', icon: AlertTriangle },
  { key: 'stage', label: 'Pipeline Stage', icon: GitMerge }
];

export const MergePanel: React.FC<MergePanelProps> = ({
  set,
  onMerge,
  onCancel,
  isLoading = false
}) => {
  // State for primary lead selection
  const [primaryLeadId, setPrimaryLeadId] = useState<string>(
    set.primaryLeadId || set.members[0]?.leadId || ''
  );

  // State for field resolutions
  const [fieldResolutions, setFieldResolutions] = useState<Record<string, string>>({});

  // State for delete secondary option
  const [deleteSecondary, setDeleteSecondary] = useState(false);

  // State for preview toggle
  const [showPreview, setShowPreview] = useState(false);

  // Get leads from members
  const leads = useMemo(() =>
    set.members.map(m => m.lead),
    [set.members]
  );

  // Get primary lead
  const primaryLead = useMemo(() =>
    leads.find(l => l.id === primaryLeadId),
    [leads, primaryLeadId]
  );

  // Initialize field resolutions with primary lead values
  React.useEffect(() => {
    if (primaryLeadId && !Object.keys(fieldResolutions).length) {
      const initialResolutions: Record<string, string> = {};
      MERGEABLE_FIELDS.forEach(field => {
        initialResolutions[field.key] = primaryLeadId;
      });
      setFieldResolutions(initialResolutions);
    }
  }, [primaryLeadId]);

  // Handle field selection
  const handleFieldSelect = (field: string, leadId: string) => {
    setFieldResolutions(prev => ({
      ...prev,
      [field]: leadId
    }));
  };

  // Get merged result preview
  const getMergedPreview = useMemo(() => {
    const preview: Record<string, any> = {};

    MERGEABLE_FIELDS.forEach(field => {
      const selectedLeadId = fieldResolutions[field.key] || primaryLeadId;
      const selectedLead = leads.find(l => l.id === selectedLeadId);
      if (selectedLead) {
        preview[field.key] = (selectedLead as any)[field.key];
      }
    });

    return preview;
  }, [fieldResolutions, primaryLeadId, leads]);

  // Handle merge
  const handleMerge = () => {
    const config: MergeConfig = {
      primaryLeadId,
      fieldResolutions,
      deleteSecondary
    };
    onMerge(config);
  };

  // Check if ready to merge
  const canMerge = primaryLeadId && Object.keys(fieldResolutions).length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <GitMerge className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Merge Duplicates</h2>
            <p className="text-sm text-gray-600">
              Combine {leads.length} leads into one
            </p>
          </div>
        </div>
      </div>

      {/* Primary lead selection */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          1. Select Primary Lead
        </h3>
        <p className="text-xs text-gray-600 mb-4">
          The primary lead will be kept after merge. Other leads' data will be merged into it.
        </p>

        <div className="space-y-2">
          {leads.map(lead => (
            <button
              key={lead.id}
              onClick={() => {
                setPrimaryLeadId(lead.id);
                // Reset resolutions to new primary
                const newResolutions: Record<string, string> = {};
                MERGEABLE_FIELDS.forEach(field => {
                  newResolutions[field.key] = lead.id;
                });
                setFieldResolutions(newResolutions);
              }}
              className={cn(
                "w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all",
                primaryLeadId === lead.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              )}
            >
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  primaryLeadId === lead.id
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-600"
                )}>
                  {lead.name.charAt(0)}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                  <p className="text-xs text-gray-500">{lead.company} - {lead.email || 'No email'}</p>
                </div>
              </div>

              {primaryLeadId === lead.id && (
                <CheckCircle className="h-5 w-5 text-blue-500" />
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* Field resolution */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          2. Resolve Field Conflicts
        </h3>
        <p className="text-xs text-gray-600 mb-4">
          Choose which value to keep for each field. Fields with different values are highlighted.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MERGEABLE_FIELDS.map(field => {
            const values = leads.map(lead => ({
              leadId: lead.id,
              leadName: lead.name,
              value: (lead as any)[field.key]
            }));

            return (
              <FieldResolver
                key={field.key}
                field={field.key}
                label={field.label}
                values={values}
                selected={fieldResolutions[field.key] || primaryLeadId}
                onSelect={(leadId) => handleFieldSelect(field.key, leadId)}
              />
            );
          })}
        </div>
      </Card>

      {/* Delete secondary option */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          3. After Merge
        </h3>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={deleteSecondary}
            onChange={(e) => setDeleteSecondary(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Delete merged leads
            </p>
            <p className="text-xs text-gray-500">
              Secondary leads will be permanently deleted after merge. Activities, notes, and tasks will be moved to the primary lead.
            </p>
          </div>
        </label>

        {deleteSecondary && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Warning</p>
                <p className="text-xs text-red-600">
                  {leads.length - 1} lead(s) will be permanently deleted. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Preview */}
      <Card className="p-4">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="w-full flex items-center justify-between text-sm font-semibold text-gray-900"
        >
          <div className="flex items-center space-x-2">
            <Eye className="h-4 w-4" />
            <span>Preview Merged Lead</span>
          </div>
          {showPreview ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {showPreview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="grid grid-cols-2 gap-3 text-sm">
              {MERGEABLE_FIELDS.map(field => (
                <div key={field.key}>
                  <span className="text-gray-500">{field.label}:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {getMergedPreview[field.key] || '-'}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Will be moved to primary lead:</strong> All activities, notes, tasks, and files from secondary leads.
              </p>
            </div>
          </motion.div>
        )}
      </Card>

      {/* Action buttons */}
      <div className="flex space-x-3">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleMerge}
          disabled={!canMerge || isLoading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Merging...
            </>
          ) : (
            <>
              <GitMerge className="h-4 w-4 mr-2" />
              Merge Leads
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
